import React, {useState, useEffect, useContext} from 'react';
import '../../BidmiiCustomLoader/SmallAnimatedLoader.css';
import Grid from '@mui/material/Grid';
import Slide from '@mui/material/Slide';
import Typography from '@mui/material/Typography';
import ActionButtons from './ActionButtons';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import {
  Paper,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import TableBody from '@mui/material/TableBody';
import {currencyFormat} from '../../../util/CurrencyFormatter';
import {
  generateProjectProposalEstimateDescription,
  generateProjectProposalEstimateDetails,
} from '../../../util/AiTextGeneration';
import {NewProjectContext} from '../utils/NewProjectContext';
import {makeStyles} from '@mui/styles';
import PrimaryButton from '../../../common/StyledComponents/PrimaryButton';
import Skeleton from '@mui/material/Skeleton';
import useSnackbar from '../../../hooks/useSnackbar';

interface IProps {
  changeStepHandler: (number: number) => void;
  forward: boolean;
}

interface ITaxRegion {
  regionCode: string;
  title: string;
  percent: number;
}

interface IEstimate {
  title: string;
  description: string;
  totalMaterials: number;
  totalLabour: number;
  totalTaxes: number;
  totalAmount: number;
  subtotal: number;
  details: ILineItem[];
  taxRegion: ITaxRegion;
}

interface ILineItem {
  name: string;
  materials: number;
  labour: number;
  amount: number;
}

const useStyles = makeStyles((theme) => ({
  container: {
    marginLeft: '15%',
    marginRight: '15%',
    marginBottom: '15%',
    minWidth: '100%',

    [theme.breakpoints.up('sm')]: {
      marginLeft: '2.5%',
      marginRight: '2.5%',
    },

    [theme.breakpoints.up('md')]: {
      minWidth: 700,
      marginLeft: '15%',
      marginRight: '15%',
    },

    [theme.breakpoints.up('lg')]: {
      marginLeft: '20%',
      marginRight: '20%',
    },

    [theme.breakpoints.up('xl')]: {
      marginLeft: '25%',
      marginRight: '25%',
    },
  },
  print: {
    paddingTop: '15%',
    paddingLeft: '5%',
    paddingRight: '5%',

    [theme.breakpoints.down('md')]: {
      paddingTop: '5%',
      paddingLeft: '0%',
      paddingRight: '0%',
    },
  },
  printTable: {
    paddingTop: '5%',
    paddingBottom: '15%',
    paddingLeft: '5%',
    paddingRight: '5%',

    [theme.breakpoints.down('md')]: {
      paddingTop: '5%',
      paddingBottom: '15%',
      paddingLeft: '0%',
      paddingRight: '0%',
    },
  },
}));

const ProjectEstimate = ({changeStepHandler, forward}: IProps) => {
  const {project} = useContext(NewProjectContext);
  const classes = useStyles();

  const {openSnackbar} = useSnackbar();
  const [AILoading, setAILoading] = useState(false);
  const [description, setDescription] = useState<string | null>();
  const [rows, setRows] = useState<ILineItem[]>([]);
  const [estimate, setEstimate] = useState<IEstimate>();

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://html2canvas.hertzen.com/dist/html2canvas.min.js';
    script.async = true;

    script.onload = () => {
      console.log('Script loaded successfully: html2canvas');
    };

    script.onerror = () => {
      console.error('Failed to load script: html2canvas');
    };

    document.body.appendChild(script);

    const script2 = document.createElement('script');
    script2.src =
      'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
    script2.async = true;

    script2.onload = () => {
      console.log('Script loaded successfully: jspdf');
    };

    script2.onerror = () => {
      console.error('Failed to load script: jspdf');
    };

    document.body.appendChild(script2);

    // Clean up the script when the component unmounts
    return () => {
      document.body.removeChild(script);
      document.body.removeChild(script2);
    };
  }, []);

  const generateEstimate = () => {
    setAILoading(true);

    generateProjectProposalEstimateDescription(project.uid).then((res) => {
      setAILoading(false);
      if (res) {
        setDescription(res);
      } else {
        openSnackbar(
          'The server had an error while processing your request. Please try again.',
          'error',
        );
      }
    });
  };

  useEffect(() => {
    if (description) {
      generateProjectProposalEstimateDetails(project.uid, description).then(
        (res) => {
          setAILoading(false);
          if (res) {
            setRows(res.details);
            setEstimate(res);
          } else {
            openSnackbar(
              'The server had an error while processing your request. Please try again.',
              'error',
            );
          }
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [description]);

  useEffect(() => {
    generateEstimate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClickNext = () => {
    changeStepHandler(1);
  };

  const addFooter = (pdf: any) => {
    // Set the font size and color
    pdf.setFontSize(10);
    pdf.setTextColor(128, 128, 128);

    // Get the page width
    const pdfWidth = pdf.internal.pageSize.getWidth();

    // Calculate the x-position for right alignment
    const xPosition =
      pdfWidth - pdf.getTextWidth('Powered by Bidmii, https://bidmii.com') - 10;

    // Add the footer text
    pdf.text(
      'Prepared by Bidmii, https://bidmii.com',
      xPosition,
      pdf.internal.pageSize.getHeight() - 10,
    );
  };

  const handleClickDownload = async () => {
    // Get the elements
    const descriptionElement = document.getElementById('estimate-container');
    const tableElement = document.getElementById('estimate-table-container');

    // Render the description to an image using html2canvas
    // @ts-ignore
    const descriptionCanvas = await html2canvas(descriptionElement);
    const descriptionImageData = descriptionCanvas.toDataURL('image/png');

    // Render the table to an image using html2canvas
    // @ts-ignore
    const tableCanvas = await html2canvas(tableElement);
    const tableImageData = tableCanvas.toDataURL('image/png');

    // Create a jsPDF instance (A4 size)
    // @ts-ignore
    const pdf = new jspdf.jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    // Get PDF page dimensions
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    // Calculate margins (adjust as needed)
    const margin = 10;

    // Handle the Description ---

    // Image dimensions for description
    const descImgWidth = descriptionCanvas.width;
    const descImgHeight = descriptionCanvas.height;

    // Calculate scaling for description
    const descWidthRatio = (pdfWidth - 2 * margin) / descImgWidth;
    const descHeightRatio = (pdfHeight - 2 * margin) / descImgHeight;
    const descRatio = Math.min(descWidthRatio, descHeightRatio);

    const descScaledImgWidth = descImgWidth * descRatio;
    const descScaledImgHeight = descImgHeight * descRatio;

    // Add the description image to the PDF (second page)
    pdf.addImage(
      descriptionImageData,
      'PNG',
      margin,
      margin,
      descScaledImgWidth,
      descScaledImgHeight,
    );

    // Load the cover image
    const coverImage = new Image();
    coverImage.src =
      'https://cdn.bidmii.com/media/web_assets/instant_estimate_1.png';

    // Wait for the cover image to load
    await new Promise<void>((resolve) => {
      coverImage.onload = () => resolve();
    });

    const pixelsPerMm = 96 / 25.4;

    // Convert image dimensions to millimeters
    const coverImageWidthMm = coverImage.width / pixelsPerMm;
    const coverImageHeightMm = coverImage.height / pixelsPerMm;

    // Add the cover image to the first page, centered horizontally and vertically
    pdf.addImage(
      coverImage,
      'PNG',
      (pdfWidth - coverImageWidthMm) / 2,
      0,
      coverImageWidthMm,
      coverImageHeightMm,
    );

    // Handle the Table (New Page) ---

    // Add a new page for the table
    pdf.addPage();
    addFooter(pdf);

    // Image dimensions for table
    const tableImgWidth = tableCanvas.width;
    const tableImgHeight = tableCanvas.height;

    // Calculate scaling for table
    const tableWidthRatio = (pdfWidth - 2 * margin) / tableImgWidth;
    const tableHeightRatio = (pdfHeight - 2 * margin) / tableImgHeight;
    const tableRatio = Math.min(tableWidthRatio, tableHeightRatio);

    const tableScaledImgWidth = tableImgWidth * tableRatio;
    const tableScaledImgHeight = tableImgHeight * tableRatio;

    // Add the table image to the PDF (third page)
    pdf.addImage(
      tableImageData,
      'PNG',
      margin,
      margin,
      tableScaledImgWidth,
      tableScaledImgHeight,
    );

    // Save the PDF
    pdf.save('bidmii_estimate.pdf');
  };

  return (
    <Slide in={true} direction={forward ? 'left' : 'right'} timeout={600}>
      <div
        className={classes.container}
        style={{marginTop: AILoading ? 40 : 0}}>
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          style={{
            width: 'auto',
            minWidth: '100%',
            marginBottom: description ? 0 : 30,
          }}>
          <Grid item>
            <Typography
              variant="h6"
              fontSize={22}
              lineHeight={1.4}
              mb={1}
              fontWeight="bold">
              {description ? '' : 'Calculating Estimate'}
            </Typography>
          </Grid>
          {AILoading && (
            <>
              <Grid item>
                <div
                  style={{
                    display: 'flex',
                    marginLeft: 4,
                    marginRight: 53,
                  }}>
                  <div className="smallDot" />
                  <div className="smallDot" />
                  <div className="smallDot" />
                  <div className="smallDot" />
                  <div className="smallDot" />
                </div>
              </Grid>
            </>
          )}
        </Grid>
        {AILoading && (
          <>
            <div>
              <Skeleton
                variant="text"
                width={'30%'}
                height={26}
                style={{marginBottom: 5}}
              />
              <Skeleton
                variant="text"
                width={'100%'}
                height={23}
                style={{marginBottom: 5}}
              />
              <Skeleton
                variant="text"
                width={'100%'}
                height={23}
                style={{marginBottom: 5}}
              />
              <Skeleton
                variant="text"
                width={'100%'}
                height={23}
                style={{marginBottom: 5}}
              />
              <Skeleton
                variant="text"
                width={'100%'}
                height={23}
                style={{marginBottom: 5}}
              />
              <Skeleton
                variant="text"
                width={'100%'}
                height={23}
                style={{marginBottom: 25}}
              />
              <Skeleton
                variant="text"
                width={'25%'}
                height={26}
                style={{marginBottom: 5}}
              />
              <Skeleton
                variant="text"
                width={'100%'}
                height={23}
                style={{marginBottom: 5}}
              />
              <Skeleton
                variant="text"
                width={'100%'}
                height={23}
                style={{marginBottom: 5}}
              />
              <Skeleton
                variant="text"
                width={'100%'}
                height={23}
                style={{marginBottom: 5}}
              />
              <Skeleton
                variant="text"
                width={'100%'}
                height={23}
                style={{marginBottom: 5}}
              />
              <Skeleton
                variant="text"
                width={'100%'}
                height={23}
                style={{marginBottom: 5}}
              />
              <Skeleton
                variant="text"
                width={'100%'}
                height={23}
                style={{marginBottom: 5}}
              />
              <Skeleton
                variant="text"
                width={'100%'}
                height={23}
                style={{marginBottom: 5}}
              />
              <Skeleton
                variant="text"
                width={'100%'}
                height={23}
                style={{marginBottom: 5}}
              />
            </div>
          </>
        )}
        {description && (
          <div id={'estimate-container'} className={classes.print}>
            <Typography
              variant="h6"
              fontSize={22}
              lineHeight={1.4}
              mb={1}
              fontWeight="bold">
              Scope Of Work
            </Typography>
            <div dangerouslySetInnerHTML={{__html: description}} />
            <div>
              <b>Disclaimer:</b> Think of this estimate as an AI assisted guess
              — it’s good, but it can’t walk through your house or chat about
              your favorite colour. For accurate pricing, meet the pros in
              person. Post your project on Bidmii.com and connect with local,
              community-vetted contractors who can do just that.
            </div>
          </div>
        )}
        {description && (
          <div id={'estimate-table-container'} className={classes.printTable}>
            {estimate ? (
              <TableContainer
                component={Paper}
                style={{backgroundColor: '#fff'}}>
                <Table
                  sx={{minWidth: 300}}
                  aria-label="spanning table"
                  style={{backgroundColor: '#fff'}}>
                  <TableHead style={{backgroundColor: '#fff'}}>
                    <TableRow style={{backgroundColor: '#fff'}}>
                      <TableCell
                        align="center"
                        colSpan={3}
                        style={{backgroundColor: '#fff'}}>
                        Details
                      </TableCell>
                      <TableCell
                        align="right"
                        style={{backgroundColor: '#fff'}}>
                        Price
                      </TableCell>
                    </TableRow>
                    <TableRow style={{backgroundColor: '#fff'}}>
                      <TableCell style={{backgroundColor: '#fff'}}>
                        Desc
                      </TableCell>
                      <TableCell
                        align="right"
                        style={{backgroundColor: '#fff'}}>
                        Materials
                      </TableCell>
                      <TableCell
                        align="right"
                        style={{backgroundColor: '#fff'}}>
                        Labour
                      </TableCell>
                      <TableCell
                        align="right"
                        style={{backgroundColor: '#fff'}}>
                        Cost
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody style={{backgroundColor: '#fff'}}>
                    {rows.map((row, index) => (
                      <TableRow
                        key={`${row.name}_${index}`}
                        style={{backgroundColor: '#fff'}}>
                        <TableCell style={{backgroundColor: '#fff'}}>
                          {row.name}
                        </TableCell>
                        <TableCell
                          align="right"
                          style={{backgroundColor: '#fff'}}>
                          {currencyFormat(row.materials, true)}
                        </TableCell>
                        <TableCell
                          align="right"
                          style={{backgroundColor: '#fff'}}>
                          {currencyFormat(row.labour, true)}
                        </TableCell>
                        <TableCell
                          align="right"
                          style={{backgroundColor: '#fff'}}>
                          {currencyFormat(row.amount, true)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow style={{backgroundColor: '#fff'}}>
                      <TableCell
                        rowSpan={3}
                        style={{backgroundColor: '#fff'}}
                      />
                      <TableCell
                        colSpan={2}
                        style={{fontWeight: 'bold', backgroundColor: '#fff'}}>
                        Subtotal
                      </TableCell>
                      <TableCell
                        align="right"
                        style={{backgroundColor: '#fff'}}>
                        {currencyFormat(estimate?.subtotal, true)}
                      </TableCell>
                    </TableRow>
                    <TableRow style={{backgroundColor: '#fff'}}>
                      <TableCell
                        style={{fontWeight: 'bold', backgroundColor: '#fff'}}>
                        Tax
                      </TableCell>
                      <TableCell
                        align="right"
                        style={{
                          backgroundColor: '#fff',
                        }}>{`${(estimate?.taxRegion?.percent * 100).toFixed(0)} %`}</TableCell>
                      <TableCell
                        align="right"
                        style={{backgroundColor: '#fff'}}>
                        {currencyFormat(estimate?.totalTaxes, true)}
                      </TableCell>
                    </TableRow>
                    <TableRow style={{backgroundColor: '#fff'}}>
                      <TableCell
                        colSpan={2}
                        style={{fontWeight: 'bold', backgroundColor: '#fff'}}>
                        Total
                      </TableCell>
                      <TableCell
                        align="right"
                        style={{backgroundColor: '#fff'}}>
                        {currencyFormat(estimate?.totalAmount, true)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <>
                <div>
                  <Skeleton
                    variant="text"
                    width={'100%'}
                    height={23}
                    style={{marginBottom: 5}}
                  />
                  <Skeleton
                    variant="text"
                    width={'100%'}
                    height={23}
                    style={{marginBottom: 5}}
                  />
                  <Skeleton
                    variant="text"
                    width={'100%'}
                    height={23}
                    style={{marginBottom: 5}}
                  />
                  <Skeleton
                    variant="text"
                    width={'100%'}
                    height={23}
                    style={{marginBottom: 5}}
                  />
                  <Skeleton
                    variant="text"
                    width={'30%'}
                    height={23}
                    style={{marginBottom: 5, marginLeft: 'auto'}}
                  />
                  <Skeleton
                    variant="text"
                    width={'30%'}
                    height={23}
                    style={{marginBottom: 5, marginLeft: 'auto'}}
                  />
                  <Skeleton
                    variant="text"
                    width={'30%'}
                    height={23}
                    style={{marginBottom: 5, marginLeft: 'auto'}}
                  />
                </div>
              </>
            )}
          </div>
        )}
        {estimate && (
          <div>
            <Grid container justifyContent={'space-between'}>
              <Grid item></Grid>
              <Grid item>
                <PrimaryButton
                  variant={'contained'}
                  onClick={handleClickDownload}>
                  Download PDF <CloudDownloadIcon />
                </PrimaryButton>
              </Grid>
            </Grid>
          </div>
        )}
        <ActionButtons
          secondaryAction={() => changeStepHandler(-1)}
          primaryAction={handleClickNext}
          loading={false}
        />
      </div>
    </Slide>
  );
};

export default ProjectEstimate;
